import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useTenant } from '../TenantContext';
import { CircularProgress, Box } from '@mui/material';
import { isDeployedEnv } from '../../lib/envDetection';
import { decodeJwtPayload, readSupabaseTokensFromLocalStorage } from '../../lib/supabaseAuth';
// RBAC Types (inlined to avoid module resolution issues)
type Role = 'super_admin' | 'admin' | 'accounting' | 'insurance' | 'readonly';
type Portal = 'admin' | 'reports' | 'sales' | 'accounting' | 'insurance';
type Feature = 'parts' | 'chemicals';

// Safe localStorage getter that handles Safari private mode and storage-blocked contexts
const safeGetLocalStorage = (key: string): string | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

// Debug logging - only in dev or when explicitly enabled
const DEBUG = import.meta.env.DEV || safeGetLocalStorage('DEBUG_PROTECTED_ROUTE') === 'true';

const debugLog = (...args: unknown[]) => {
  if (DEBUG) console.log(...args);
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  requireInsurancePartner?: boolean;
  /** Require user to have one of these roles */
  requireRole?: Role | Role[];
  /** Require user to have access to this portal */
  requirePortal?: Portal | string;
  /** Require the current tenant to have this feature AND the user to have it in scope. */
  requireFeature?: Feature;
}

/**
 * ProtectedRoute component that enforces authentication and authorization
 *
 * Security features:
 * - Requires valid user AND session in deployed environments
 * - Prevents auth bypass attempts by checking hostname
 * - Supports super admin requirement
 * - Supports role-based access control (RBAC)
 * - Supports portal-based access control
 * - Logs unauthorized access attempts
 *
 * @param children - The component to render if authorized
 * @param requireSuperAdmin - Whether to require super admin privileges
 * @param requireInsurancePartner - Whether to require insurance partner role
 * @param requireRole - Require user to have one of these roles
 * @param requirePortal - Require user to have access to this portal
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSuperAdmin = false,
  requireInsurancePartner = false,
  requireRole,
  requirePortal,
  requireFeature,
}) => {
  const {
    user,
    session,
    loading,
    isSuperAdmin,
    isInsurancePartner,
    insurancePartnerLoading,
    role,
    roleLoading,
    canAccessPortal,
  } = useAuth();
  const { hasFeature, featuresLoading, currentTenant } = useTenant();
  const location = useLocation();
  const [checkingAdmin, setCheckingAdmin] = React.useState(requireSuperAdmin);

  const isDeployed = isDeployedEnv();

  // For super admin routes, wait for the status to load
  // Cancel waiting early if isSuperAdmin becomes true
  React.useEffect(() => {
    if (requireSuperAdmin && !loading) {
      // If we already have super admin status, no need to wait
      if (isSuperAdmin) {
        debugLog('[ProtectedRoute] Super admin status confirmed, proceeding immediately');
        setCheckingAdmin(false);
        return;
      }

      // Give the super admin check up to 5 seconds to complete
      debugLog('[ProtectedRoute] Waiting for super admin status check...');
      const timer = setTimeout(() => {
        debugLog('[ProtectedRoute] Timeout reached, checking isSuperAdmin:', isSuperAdmin);
        setCheckingAdmin(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else if (!requireSuperAdmin) {
      setCheckingAdmin(false);
    }
  }, [requireSuperAdmin, loading, isSuperAdmin]);

  // Determine if we need to wait for role loading
  // Also wait if role is null but we need a role check — role may still be arriving
  const needsRoleCheck = requireRole || requirePortal;
  const isRoleLoading = needsRoleCheck && (roleLoading || (!role && !isSuperAdmin));

  // Wait for feature data if a feature is required and the tenant context is still loading.
  // If a feature is required but no tenant is selected yet, also wait — the tenant context
  // is still figuring out which tenant is current.
  const needsFeatureCheck = !!requireFeature;
  const isFeatureLoading = needsFeatureCheck && (featuresLoading || !currentTenant);

  // ORDER MATTERS — resolve identity before waiting on identity's attributes:
  // 1) loading: auth state genuinely unknown, show spinner
  // 2) no user: signed out → /login (must run BEFORE role/feature waits,
  //    which contain (!role && !isSuperAdmin) — permanently true without a user)
  // 3) aal1: mid-MFA → challenge (role waits can never resolve at aal1 either)
  // 4) only then wait on role/feature loading for a real aal2 user

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // CRITICAL: In deployed environments, require BOTH user AND session
  // This prevents auth bypass attempts
  // Check for proxy auth session before redirecting
  const hasProxySession = (() => {
    try {
      return !!(safeGetLocalStorage('afl_user_id') && safeGetLocalStorage('PW_ACCESS_TOKEN'));
    } catch {
      return false;
    }
  })();

  if (!user || (isDeployed && !session && !hasProxySession)) {
    debugLog('[ProtectedRoute] Redirecting to login - no active session', {
      hasUser: !!user,
      hasSession: !!session,
      hasProxySession,
      isDeployed,
      path: location.pathname,
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // An aal1 session is mid-MFA — send it to the challenge instead of
  // spinning on role gates it can never satisfy.
  const { accessToken: storedToken } = readSupabaseTokensFromLocalStorage(
    (import.meta as any).env?.VITE_SUPABASE_URL,
  );
  const tokenAal = storedToken
    ? (decodeJwtPayload(storedToken)?.aal as string | undefined)
    : undefined;
  if (tokenAal === 'aal1') {
    debugLog('[ProtectedRoute] aal1 session on protected route, redirecting to MFA', {
      path: location.pathname,
    });
    return <Navigate to={`/auth/mfa?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (
    (requireSuperAdmin && checkingAdmin && !isSuperAdmin) ||
    (requireInsurancePartner && insurancePartnerLoading) ||
    isRoleLoading ||
    isFeatureLoading
  ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Check super admin requirement
  if (requireSuperAdmin && !isSuperAdmin) {
    debugLog('[ProtectedRoute] Super admin access required, redirecting', {
      user: user.email,
      path: location.pathname,
      isSuperAdmin,
    });
    return <Navigate to="/" replace />;
  }

  // Check insurance partner requirement (super admins are allowed for preview/testing)
  if (requireInsurancePartner && !isSuperAdmin && !isInsurancePartner) {
    debugLog('[ProtectedRoute] Insurance partner access required, redirecting', {
      user: user.email,
      path: location.pathname,
      isSuperAdmin,
      isInsurancePartner,
    });
    return <Navigate to="/" replace />;
  }

  // Check role requirement (super admins bypass role checks)
  if (requireRole && !isSuperAdmin) {
    const requiredRoles = Array.isArray(requireRole) ? requireRole : [requireRole];
    const hasRequiredRole = role && requiredRoles.includes(role);

    if (!hasRequiredRole) {
      debugLog('[ProtectedRoute] Role access required, redirecting', {
        user: user.email,
        path: location.pathname,
        userRole: role,
        requiredRoles,
      });
      return <Navigate to="/" replace />;
    }
  }

  // Check portal requirement (super admins bypass portal checks)
  if (requirePortal && !isSuperAdmin) {
    const hasPortalAccess = canAccessPortal(requirePortal);

    if (!hasPortalAccess) {
      debugLog('[ProtectedRoute] Portal access required, redirecting', {
        user: user.email,
        path: location.pathname,
        userRole: role,
        requiredPortal: requirePortal,
      });
      return <Navigate to="/" replace />;
    }
  }

  // Check feature requirement. Hides the route entirely if the user lacks access.
  // hasFeature returns false when:
  //   - Tenant doesn't have the feature enabled, OR
  //   - User is an insurance partner whose feature_scope doesn't include it
  // Admins, super admins, and accounting users get the full set of tenant features.
  if (requireFeature && !hasFeature(requireFeature)) {
    debugLog('[ProtectedRoute] Feature access required, redirecting', {
      user: user.email,
      path: location.pathname,
      requiredFeature: requireFeature,
    });
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
